import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import {
  X,
  Home,
  Layers,
  BookOpen,
  FileText,
  HelpCircle,
  Newspaper,
  Mail,
  Phone,
  ExternalLink,
  Shield,
  User,
  LogOut,
  FolderKanban,
  MessageSquare,
  Settings,
  Sparkles,
  ChevronRight,
  Globe,
  Handshake,
  Languages,
  DollarSign,
  Receipt,
  ShieldCheck
} from 'lucide-react';
import { AbnyWebBadge } from '../common/AbnyWebBadge';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'light' | 'dark';
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  theme = 'light',
}) => {
  const { user, isAuthenticated, isStaff, logout } = useAuth();
  const location = useLocation();
  const isDark = theme === 'dark';

  if (!isOpen) return null;

  const publicLinks = [
    { label: 'Home', path: '/', icon: Home },
    { label: 'About Publishing House', path: '/about', icon: Sparkles },
    { label: 'Publishing Services', path: '/services', icon: Layers },
    { label: 'For Authors & Guidelines', path: '/for-authors', icon: FileText },
    { label: '12-Stage Publishing Pathway', path: '/our-process', icon: FolderKanban },
    { label: 'Published Works Catalog', path: '/publications', icon: BookOpen },
    { label: 'Author Resource Centre', path: '/resources', icon: HelpCircle },
    { label: 'News & Publishing Insights', path: '/news', icon: Newspaper },
    { label: 'Editorial Inquiries & Contact', path: '/contact', icon: Mail },
  ];

  const clientLinks = [
    { label: 'Dashboard Overview', path: '/client/dashboard', icon: Home },
    { label: 'Publishing Projects', path: '/client/projects', icon: FolderKanban },
    { label: 'Vernacular Translations', path: '/client/translations', icon: Languages },
    { label: 'Invoices & Billing', path: '/client/invoices', icon: Receipt },
    { label: 'Document & Proofs Centre', path: '/client/documents', icon: FileText },
    { label: 'Editorial Communications', path: '/client/messages', icon: MessageSquare },
    { label: 'Author Account Settings', path: '/client/settings', icon: Settings },
  ];

  const adminLinks = [
    { label: 'Admin Dashboard', path: '/admin', icon: Home },
    { label: 'Manuscript Enquiries & RFQ', path: '/admin/enquiries', icon: Mail },
    { label: 'Partners CRM', path: '/admin/partners', icon: Handshake },
    { label: 'Customers & Authors CRM', path: '/admin/customers', icon: User },
    { label: 'Vernacular Translations', path: '/admin/translations', icon: Languages },
    { label: 'Revenue & Financials', path: '/admin/revenue', icon: Receipt },
    { label: 'Staff & Role Directory', path: '/admin/staff', icon: ShieldCheck },
    { label: 'Project Milestones', path: '/admin/projects', icon: FolderKanban },
    { label: 'Global Document Repository', path: '/admin/documents', icon: FileText },
    { label: 'Client Messages Hub', path: '/admin/messages', icon: MessageSquare },
    { label: 'Publishing Services CMS', path: '/admin/cms/services', icon: Layers },
    { label: 'Published Works CMS', path: '/admin/cms/publications', icon: BookOpen },
    { label: 'Domain Candidates Planning', path: '/admin/domains', icon: Globe },
    { label: 'System Audit Logs', path: '/admin/activity-logs', icon: Shield },
  ];

  const isClient = location.pathname.startsWith('/client');
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-200 font-sans">
      
      {/* Backdrop tap to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Bottom Sheet Modal Container */}
      <div
        className={`relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl transition-transform animate-in slide-in-from-bottom duration-300 ${
          isDark
            ? 'bg-slate-950 border border-slate-800 text-white'
            : 'bg-white border border-slate-200 text-slate-900'
        }`}
      >
        {/* Drag Handle Indicator */}
        <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mb-4" />

        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4 mb-4 border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full overflow-hidden border border-brand-500/50 bg-white p-0.5 shadow-xs flex items-center justify-center">
              <img
                src="/ftt-logo.png"
                alt="For The Truth Logo"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
            <div>
              <h3 className="font-extrabold text-sm tracking-tight">FOR THE TRUTH PRESS</h3>
              <p className="text-[10px] font-mono text-slate-500 dark:text-slate-400">
                {isAdmin ? 'Admin Operations Backend' : isClient ? 'Author Client Portal' : 'Official Publishing Platform'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Card if Authenticated */}
        {isAuthenticated && (
          <div className="p-3.5 mb-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-brand-700 text-white flex items-center justify-center font-bold text-xs">
                {user?.firstName?.[0] || 'U'}
              </div>
              <div className="overflow-hidden">
                <div className="text-xs font-bold truncate">{user?.firstName} {user?.lastName}</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 font-mono truncate">{user?.email}</div>
              </div>
            </div>
            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase bg-brand-100 dark:bg-brand-950 text-brand-800 dark:text-brand-300">
              {user?.role}
            </span>
          </div>
        )}

        {/* Active Portal Nav Links */}
        <div className="space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold px-2 block mb-1">
            {isAdmin ? 'Admin Modules' : isClient ? 'Author Features' : 'Publishing Sections'}
          </span>

          {(isAdmin ? adminLinks : isClient ? clientLinks : publicLinks).map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold tracking-tight transition-colors ${
                  active
                    ? 'bg-brand-600 text-white font-bold'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 opacity-50" />
              </Link>
            );
          })}
        </div>

        {/* Powered by ABNY Web Pill in Drawer */}
        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-center">
          <AbnyWebBadge size="md" className="w-full justify-between" />
        </div>

        {/* Direct Contact Block */}
        <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block">
            Write To Us
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <a
              href="tel:+918604685533"
              className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-semibold text-slate-800 dark:text-slate-200"
            >
              <Phone className="w-3.5 h-3.5 text-brand-500 shrink-0" />
              <span>+91 86046 85533</span>
            </a>
            <a
              href="mailto:contact@forthetruth.in"
              className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-semibold text-slate-800 dark:text-slate-200"
            >
              <Mail className="w-3.5 h-3.5 text-brand-500 shrink-0" />
              <span className="truncate">contact@forthetruth.in</span>
            </a>
          </div>
        </div>

        {/* Ecosystem Cross-Links & Role Switching */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold px-2 block">
            Ecosystem &amp; Portals
          </span>

          {isStaff && !isAdmin && (
            <Link
              to="/admin"
              onClick={onClose}
              className="flex items-center justify-between p-3 rounded-xl bg-amber-950/20 border border-amber-800/40 text-amber-700 dark:text-amber-300 text-xs font-bold"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Switch to Admin Panel</span>
              </div>
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}

          {isAuthenticated && (isAdmin || !isClient) && (
            <Link
              to="/client/dashboard"
              onClick={onClose}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs font-bold"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Switch to Author Portal</span>
              </div>
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}

          {(isClient || isAdmin) && (
            <Link
              to="/"
              onClick={onClose}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs font-bold"
            >
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4" />
                <span>View Public Website</span>
              </div>
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}

          <a
            href="https://forthetruth.in/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-brand-50 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/60 text-brand-900 dark:text-brand-300 text-xs font-bold"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Visit For The Truth Bookstore</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Action Button: Sign In / Out or Submit RFQ */}
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
          {isAuthenticated ? (
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 font-bold text-xs border border-rose-200 dark:border-rose-800"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out of Account</span>
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/login"
                onClick={onClose}
                className="py-3 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs text-center"
              >
                Sign In
              </Link>
              <Link
                to="/submit-enquiry"
                onClick={onClose}
                className="py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs text-center"
              >
                Start RFQ
              </Link>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
